import React, { useState } from 'react';
import { Subtask } from '../../types/task';
import { CheckIcon, PlusIcon, TrashIcon, ListBulletIcon, PlayIcon } from '@heroicons/react/24/outline';

interface Props {
  subtasks: Subtask[];
  onChange: (next: Subtask[]) => void;
  disabled?: boolean;
  compact?: boolean;
  /** ha megadva: ▶ gomb az alfeladat sorában, időmérő indításához */
  onStartTimer?: (subtask: Subtask) => void;
  /** melyik alfeladatra fut most időmérő */
  runningSubtaskId?: string | null;
}

const newId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

/** Alfeladatok / checklist – haladás-sávval, inline hozzáadással. */
const SubtaskList: React.FC<Props> = ({ subtasks, onChange, disabled, compact, onStartTimer, runningSubtaskId }) => {
  const [draft, setDraft] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState('');
  const done = subtasks.filter(s => s.done).length;
  const pct = subtasks.length ? Math.round((done / subtasks.length) * 100) : 0;

  /** Több sor → több alfeladat (felsorolásjelek, számozás, üres sorok és duplikátumok kiszűrve). */
  const parseLines = (text: string) => {
    const existing = new Set(subtasks.map(x => x.title.trim().toLowerCase()));
    const out: string[] = [];
    text.split(/\r?\n/).forEach(line => {
      const t = line.replace(/^\s*(?:[-*•▪]|\d+[.)]|\[[ xX]?\])\s*/, '').trim();
      if (t && !existing.has(t.toLowerCase())) { existing.add(t.toLowerCase()); out.push(t); }
    });
    return out;
  };
  const addMany = (titles: string[]) => {
    if (!titles.length) return;
    onChange([...subtasks, ...titles.map(title => ({ id: newId(), title, done: false, createdAt: new Date() }))]);
  };
  const add = () => {
    const title = draft.trim();
    if (!title) return;
    addMany(parseLines(title).length > 1 ? parseLines(title) : [title]);
    setDraft('');
  };
  const addBulk = () => { addMany(parseLines(bulk)); setBulk(''); setBulkOpen(false); };
  const toggle = (id: string) => onChange(subtasks.map(s => (s.id === id ? { ...s, done: !s.done, completedAt: !s.done ? new Date() : null } : s)));
  const remove = (id: string) => onChange(subtasks.filter(s => s.id !== id));
  const rename = (id: string, title: string) => onChange(subtasks.map(s => (s.id === id ? { ...s, title } : s)));

  return (
    <div className="space-y-2">
      {subtasks.length > 0 && (
        <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
          <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : 'bg-primary-600'}`} style={{ width: `${pct}%` }} />
          </div>
          <span>{done}/{subtasks.length} · {pct}%</span>
        </div>
      )}
      <ul className="space-y-1">
        {subtasks.map(s => (
          <li key={s.id} className="group flex items-center gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => toggle(s.id)}
              className={`w-4.5 h-4.5 w-[18px] h-[18px] rounded border flex items-center justify-center flex-shrink-0 transition-colors ${s.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 dark:border-gray-600 hover:border-emerald-500 text-transparent'}`}
              aria-label={s.done ? 'Visszanyitás' : 'Kész'}
            >
              <CheckIcon className="w-3 h-3" />
            </button>
            {compact || disabled ? (
              <span className={`text-sm flex-1 min-w-0 truncate ${s.done ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>{s.title}</span>
            ) : (
              <input
                value={s.title}
                onChange={e => rename(s.id, e.target.value)}
                className={`flex-1 min-w-0 bg-transparent border-0 border-b border-transparent focus:border-[var(--crm-border)] px-0 py-0.5 text-sm focus:ring-0 ${s.done ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}
              />
            )}
            {onStartTimer && !s.done && (
              runningSubtaskId === s.id ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 whitespace-nowrap" title="Fut az időmérő"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />mérés</span>
              ) : (
                <button type="button" onClick={() => onStartTimer(s)} className="opacity-60 sm:opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-emerald-600" aria-label="Időmérő indítása erre az alfeladatra" title="Időmérő indítása"><PlayIcon className="w-3.5 h-3.5" /></button>
              )
            )}
            {!disabled && (
              <button type="button" onClick={() => remove(s.id)} className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-rose-600" aria-label="Törlés"><TrashIcon className="w-3.5 h-3.5" /></button>
            )}
          </li>
        ))}
      </ul>
      {!disabled && !bulkOpen && (
        <div className="flex items-center gap-2">
          <PlusIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            onPaste={e => { const text = e.clipboardData.getData('text'); if (/\r?\n/.test(text.trim())) { e.preventDefault(); addMany(parseLines(text)); } }}
            placeholder="Új alfeladat… (Enter; több sor beillesztése = több alfeladat)"
            className="crm-input-raw flex-1 min-w-0 h-8 bg-transparent border-0 border-b border-[var(--crm-border)] px-0 text-sm focus:ring-0 focus:border-gray-400 placeholder-gray-400"
          />
          {draft.trim() ? (
            <button type="button" onClick={add} className="crm-btn crm-btn-secondary h-7 text-xs px-2">Hozzáad</button>
          ) : (
            <button type="button" onClick={() => setBulkOpen(true)} title="Több alfeladat egyszerre" className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"><ListBulletIcon className="w-3.5 h-3.5" />Több egyszerre</button>
          )}
        </div>
      )}
      {!disabled && bulkOpen && (
        <div className="rounded-lg border border-[var(--crm-border)] p-2 space-y-2">
          <textarea
            value={bulk}
            onChange={e => setBulk(e.target.value)}
            autoFocus
            rows={5}
            placeholder={'Soronként egy alfeladat, pl.:\nAnyag beszerzése\nHelyszíni felmérés\nSzerelés\nÁtadás, aláírás'}
            className="crm-input-raw w-full text-sm border border-[var(--crm-border)] rounded-md px-2 py-1.5 bg-[var(--crm-surface)] focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
            onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); addBulk(); } }}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-gray-500 dark:text-gray-400">{parseLines(bulk).length} új alfeladat · Ctrl+Enter</span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => { setBulkOpen(false); setBulk(''); }} className="crm-btn crm-btn-ghost h-7 text-xs px-2">Mégse</button>
              <button type="button" onClick={addBulk} disabled={!parseLines(bulk).length} className="crm-btn crm-btn-primary h-7 text-xs px-2">Hozzáadás</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubtaskList;
